import { Invoice } from '../types';
import { computeDueDate, openAmountCents, ValidationMessage } from './invoiceLogic';
import { Cents, formatCents } from './money';

/**
 * Skonto beim Zahlungseingang.
 *
 * Zahlt der Kunde innerhalb der Skontofrist, zieht er den vereinbarten Satz
 * ab — auf dem Konto kommt weniger an, als die Rechnung fordert. Ohne eigene
 * Buchung bliebe der Abzug als offener Rest stehen, die Rechnung liefe in die
 * Mahnung. Das Skonto wird deshalb als eigene Zahlungszeile mit der Zahlungsart
 * CASH_DISCOUNT gebucht: sie gleicht den Rest aus, bleibt aber von der
 * Ueberweisung unterscheidbar und laesst sich wieder loeschen.
 *
 * Entschieden am 25.09.2026 (Betreiber): jeder Betrag ist erlaubt. Liegt er
 * ueber dem vereinbarten Satz, fehlt eine Vereinbarung oder kam die Zahlung
 * nach Ablauf der Frist, gibt es eine Warnung — gesperrt wird nur, was
 * unmoeglich ist (kein Betrag, mehr als der offene Rest).
 *
 * In den DATEV-Export geht das Skonto nicht: der Export fuehrt nur
 * Rechnungen, keine Zahlungen. Die Kanzlei bucht Zahlung und Skonto aus den
 * Bankdaten (SKR04 4736 „Gewaehrte Skonti 19 % USt").
 */

type InvoiceForDiscount = Pick<
    Invoice,
    'invoiceDate' | 'details' | 'totals' | 'paidCents' | 'creditedCents'
>;

/**
 * Der Rest, der als Skonto in Frage kommt: offener Betrag minus Zahlung.
 * Null, wenn die Zahlung den offenen Betrag deckt oder uebersteigt.
 */
export const cashDiscountRemainder = (invoice: InvoiceForDiscount, paymentCents: Cents): Cents => {
    const open = openAmountCents(invoice);
    return paymentCents > 0 && paymentCents < open ? open - paymentCents : 0;
};

/** Vereinbartes Skonto der Rechnung samt Fristende, falls hinterlegt. */
export const agreedCashDiscount = (
    invoice: Pick<Invoice, 'invoiceDate' | 'details'>,
): { percent: number; days: number; deadline: string } | null => {
    const skonto = invoice.details?.skonto;
    if (!skonto || !(skonto.percent > 0) || !(skonto.days > 0) || !invoice.invoiceDate) return null;
    return {
        percent: skonto.percent,
        days: skonto.days,
        deadline: computeDueDate(invoice.invoiceDate, skonto.days),
    };
};

/** Vereinbarter Skontobetrag in Cent, bezogen auf den Zahlbetrag; null ohne Vereinbarung. */
export const agreedCashDiscountCents = (
    invoice: Pick<Invoice, 'invoiceDate' | 'details' | 'totals'>,
): Cents | null => {
    const agreed = agreedCashDiscount(invoice);
    return agreed ? Math.round((invoice.totals.payableCents * agreed.percent) / 100) : null;
};

/**
 * Ist das genau das vereinbarte Skonto — Betrag auf den Cent (ein Cent
 * Rundung des Kunden) und Zahlung in der Frist?
 *
 * Nur dann darf die Oberflaeche „entspricht dem vereinbarten Skonto" sagen.
 * checkCashDiscount prueft nur die Obergrenze: ein KLEINERER Abzug ist
 * erlaubt und ohne Warnung, aber nicht das vereinbarte Skonto.
 */
export const matchesAgreedCashDiscount = (
    invoice: Pick<Invoice, 'invoiceDate' | 'details' | 'totals'>,
    discountCents: Cents,
    valueDate: string,
): boolean => {
    const agreed = agreedCashDiscount(invoice);
    const agreedCents = agreedCashDiscountCents(invoice);
    if (!agreed || agreedCents == null) return false;
    return Math.abs(discountCents - agreedCents) <= 1 && !!valueDate && valueDate <= agreed.deadline;
};

const de = (iso: string): string => iso.split('-').reverse().join('.');

const percentText = (value: number): string =>
    value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Anteil des Skontos am Zahlbetrag der Rechnung in Prozent. */
export const cashDiscountPercent = (invoice: InvoiceForDiscount, discountCents: Cents): number =>
    invoice.totals.payableCents > 0 ? (discountCents / invoice.totals.payableCents) * 100 : 0;

/**
 * Prueft ein Skonto.
 *
 * @param alongsideCents Zahlung, die im selben Schritt gebucht wird (0, wenn
 *                       das Skonto allein gebucht wird). Beide zusammen duerfen
 *                       den offenen Betrag nicht uebersteigen.
 * @param valueDate      Wertstellung der Zahlung — gegen sie laeuft die Frist.
 */
export const checkCashDiscount = (
    invoice: InvoiceForDiscount,
    discountCents: Cents,
    valueDate: string,
    alongsideCents: Cents = 0,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    if (!Number.isSafeInteger(discountCents) || discountCents <= 0) {
        out.push({
            code: 'cash_discount_invalid',
            level: 'error',
            message: 'Das Skonto muss größer als 0,00 € sein.',
        });
        return out;
    }

    const open = openAmountCents(invoice);
    if (alongsideCents + discountCents > open) {
        out.push({
            code: 'cash_discount_exceeds_open',
            level: 'error',
            message:
                alongsideCents > 0
                    ? `Zahlung und Skonto ergeben zusammen ${formatCents(
                          alongsideCents + discountCents,
                      )}, offen sind nur ${formatCents(open)}.`
                    : `Das Skonto (${formatCents(discountCents)}) ist größer als der offene Betrag (${formatCents(
                          open,
                      )}).`,
        });
        return out;
    }

    const agreed = agreedCashDiscount(invoice);
    if (!agreed) {
        out.push({
            code: 'cash_discount_not_agreed',
            level: 'warning',
            message: 'Für diese Rechnung ist kein Skonto vereinbart.',
        });
        return out;
    }

    // Ein Cent Spielraum: der Kunde rundet seinen Abzug selbst.
    const allowed = agreedCashDiscountCents(invoice) ?? 0;
    if (discountCents > allowed + 1) {
        out.push({
            code: 'cash_discount_above_agreed',
            level: 'warning',
            message: `Das Skonto (${percentText(
                cashDiscountPercent(invoice, discountCents),
            )} %) liegt über dem vereinbarten Satz von ${percentText(agreed.percent)} % (${formatCents(
                allowed,
            )}).`,
        });
    }
    if (valueDate && valueDate > agreed.deadline) {
        out.push({
            code: 'cash_discount_after_deadline',
            level: 'warning',
            message: `Die Zahlung kam nach Ablauf der Skontofrist (${de(agreed.deadline)}).`,
        });
    }
    return out;
};
